import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { Search, RefreshCw, Users, Eye, Calendar, Filter, X } from 'lucide-react';
import CursorPagination from 'components/CursorPagination';
import { appendCursorParams, parseCursorPagination } from 'lib/cursor-pagination';
import { useCursorListPage } from 'hooks/useCursorListPage';
import { DEFAULT_PAGE_SIZE } from 'lib/page-size-options';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';
import type { Player } from 'types/admin';
import {
  USER_STATUS,
  USER_STATUS_LABEL,
  labelFor,
} from 'constants/enums';

interface PlayerFilters {
  userId: string;
  name: string;
}

const EMPTY_FILTERS: PlayerFilters = {
  userId: '',
  name: '',
};

const playerDisplayName = (player: { display_name?: string | null; username?: string }) => {
  if (player.display_name?.trim()) return player.display_name.trim();
  const username = player.username || '';
  if (!username) return '—';
  if (/^U\d+$/i.test(username)) return username;
  return username
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

function buildFilterParams(filters: PlayerFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.userId.trim()) params.set('userId', filters.userId.trim());
  if (filters.name.trim()) params.set('name', filters.name.trim());
  return params;
}

function countActiveFilters(filters: PlayerFilters): number {
  let n = 0;
  if (filters.userId.trim()) n += 1;
  if (filters.name.trim()) n += 1;
  return n;
}

const PlayerManagement = () => {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [draftFilters, setDraftFilters] = useState<PlayerFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<PlayerFilters>(EMPTY_FILTERS);

  const fetchPage = useCallback(
    async (cursor: number | null) => {
      const params = appendCursorParams(buildFilterParams(appliedFilters), cursor, pageSize);
      const res = await api.get<Player[]>(`${API_ENDPOINTS.ADMIN.PLAYERS.GET_ALL}?${params.toString()}`);
      const meta = parseCursorPagination(res);
      return {
        items: res.success && Array.isArray(res.data) ? (res.data as Player[]) : [],
        nextCursor: meta.nextCursor,
        hasMore: meta.hasMore,
        total: meta.total,
        totalPages: meta.totalPages,
      };
    },
    [appliedFilters, pageSize]
  );

  const {
    page,
    items: players,
    loading,
    refreshing,
    hasMore,
    total,
    totalPages,
    limit,
    resetAndLoad,
    refreshCurrent,
    goFirst,
    goToPage,
    goLast,
  } = useCursorListPage(fetchPage, pageSize);

  useEffect(() => {
    resetAndLoad();
  }, [appliedFilters, pageSize, resetAndLoad]);

  const updateDraft = (patch: Partial<PlayerFilters>) => {
    setDraftFilters((prev) => ({ ...prev, ...patch }));
  };

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const activeCount = players.filter((p) => p.status === USER_STATUS.ACTIVE).length;
  const suspendedCount = players.filter((p) => p.status === USER_STATUS.SUSPENDED).length;
  const activeFilterCount = countActiveFilters(appliedFilters);
  const openPlayer = (id: number) => navigate(`/players/${id}/enforcement`);
  const isEmpty = total === 0 && !loading && activeFilterCount === 0;

  return (
    <div className="space-y-5 animate-fadeIn">
      <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
        <div className="px-5 py-5 sm:px-7 sm:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-dark text-brand-gold shrink-0">
              <Users size={22} strokeWidth={2.25} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-heading tracking-tight">Players</h1>
              <p className="text-sm text-muted mt-0.5">Search and open player profiles.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-lg border border-line bg-subtle/60 px-2.5 py-1 text-xs font-semibold text-body">
              {total.toLocaleString()} total
            </span>
            <span className="rounded-lg border border-emerald-200/80 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              {activeCount} active on page
            </span>
            {suspendedCount > 0 && (
              <span className="rounded-lg border border-red-200/80 bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-700">
                {suspendedCount} suspended on page
              </span>
            )}
            <button
              type="button"
              onClick={() => refreshCurrent()}
              disabled={loading || refreshing}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line text-muted hover:text-heading hover:bg-subtle transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={15} className={loading || refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
        <div className={`${LIST_TD_CLASS} sm:px-6 flex flex-col xl:flex-row xl:items-end gap-3`}>
          <div className="flex items-center gap-2 shrink-0 xl:pb-2">
            <Filter size={16} className="text-muted" />
            <span className="text-sm font-bold text-heading">Filters</span>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-brand-gold/20 text-brand-gold text-[10px] font-bold">
                {activeFilterCount} active
              </span>
            )}
          </div>

          <div className="flex-1 flex flex-col sm:flex-row gap-3 min-w-0">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-[10px] font-bold text-muted uppercase tracking-wider mb-1.5">User ID</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="e.g. U10045"
                value={draftFilters.userId}
                onChange={(e) => updateDraft({ userId: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                className="w-full rounded-lg border border-line bg-card px-3 py-2 text-sm focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/30 font-mono"
              />
            </div>

            <div className="flex-1 min-w-[180px]">
              <label className="block text-[10px] font-bold text-muted uppercase tracking-wider mb-1.5">Username</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={15} />
                <input
                  type="text"
                  placeholder="Search username"
                  value={draftFilters.name}
                  onChange={(e) => updateDraft({ name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                  className="w-full rounded-lg border border-line bg-card pl-9 pr-3 py-2 text-sm focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/30"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={clearFilters}
              disabled={activeFilterCount === 0 && countActiveFilters(draftFilters) === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-line text-sm font-semibold text-muted hover:text-heading hover:bg-subtle transition-colors disabled:opacity-50"
            >
              <X size={14} />
              Clear
            </button>
            <button
              type="button"
              onClick={applyFilters}
              className="px-5 py-2 bg-brand-dark text-white rounded-lg text-sm font-bold hover:opacity-95 transition-opacity"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
        {loading && players.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-muted font-medium">
            <RefreshCw className="animate-spin mr-2" size={18} /> Loading players...
          </div>
        ) : isEmpty ? (
          <div className="px-5 py-14 text-center">
            <Users size={28} className="mx-auto text-muted/30 mb-2" />
            <p className="font-semibold text-body">No players yet</p>
          </div>
        ) : (
          <>
            <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between`}>
              <span className="text-xs bg-subtle text-muted px-2.5 py-1 rounded-full font-semibold">
                Page {page} of {totalPages}
              </span>
            </div>
            <div className="overflow-auto max-h-[58vh]">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-line bg-subtle/60">
                    <th className={LIST_TH_CLASS}>No.</th>
                    <th className={LIST_TH_CLASS}>User ID</th>
                    <th className={LIST_TH_CLASS}>Username</th>
                    <th className={LIST_TH_CLASS}>Player Name</th>
                    <th className={LIST_TH_CLASS}>Joined</th>
                    <th className={LIST_TH_CLASS}>Status</th>
                    <th className={LIST_TH_CLASS}>Action</th>
                  </tr>
                </thead>
                <tbody className={LIST_TBODY_CLASS}>
                  {players.length > 0 ? (
                    players.map((player, index) => (
                      <tr key={player.id} className="group hover:bg-subtle/40 transition-colors">
                        <td className={LIST_TD_NO_CLASS}>{listRowNumber(page, index, limit)}</td>
                        <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                          <button
                            type="button"
                            onClick={() => openPlayer(player.id)}
                            className="text-sm font-mono font-semibold text-heading hover:text-brand-gold hover:underline transition-colors"
                          >
                            {player.external_user_id || `U${player.id}`}
                          </button>
                        </td>
                        <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                          <span className="text-sm font-semibold text-heading">{player.username || '—'}</span>
                        </td>
                        <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                          <span className="text-sm font-semibold text-heading">{playerDisplayName(player)}</span>
                        </td>
                        <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                          {player.created_at ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-body">
                              <Calendar size={12} className="text-muted" />
                              {new Date(player.created_at).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          ) : (
                            <span className="text-xs text-muted">—</span>
                          )}
                        </td>
                        <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                          <span
                            className={`px-2 py-0.5 inline-flex text-[10px] font-bold rounded-md uppercase tracking-wide ${
                              player.status === USER_STATUS.ACTIVE
                                ? 'bg-emerald-500/10 text-emerald-700'
                                : 'bg-red-500/10 text-red-700'
                            }`}
                          >
                            {labelFor(USER_STATUS_LABEL, player.status)}
                          </span>
                        </td>
                        <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                          <button
                            type="button"
                            onClick={() => openPlayer(player.id)}
                            title="View player"
                            aria-label="View player"
                            className="inline-flex items-center justify-center p-2 bg-subtle hover:bg-subtle text-heading border border-line rounded-lg transition-all"
                          >
                            <Eye size={16} strokeWidth={2} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-5 py-14 text-center">
                        <Users size={28} className="mx-auto text-muted/30 mb-2" />
                        <p className="font-semibold text-body">No players match your filters</p>
                        <p className="text-sm text-muted mt-1">Adjust filters or clear them to see more results.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <CursorPagination
              page={page}
              totalPages={totalPages}
              total={total}
              itemCount={players.length}
              limit={limit}
              hasMore={hasMore}
              loading={loading || refreshing}
              onFirst={goFirst}
              onPageChange={goToPage}
              onLast={goLast}
              onLimitChange={setPageSize}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PlayerManagement;
